import { useState } from "react";
import { apps, type AppItem } from "@/mocks/comicData";

function AppModal({ app, onClose }: { app: AppItem; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background-950/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-lg bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-52 w-full overflow-hidden">
          <img src={app.image} alt={`${app.name} 封面`} className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent"></div>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/35"
            aria-label="关闭详情"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <span className="absolute bottom-3 left-4 font-heading text-2xl text-background-50">#{app.rank}</span>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-xl font-semibold text-foreground-900">{app.name}</h4>
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-sm text-accent-800">
              <i className="ri-star-fill text-accent-600"></i>
              {app.rating}
            </span>
          </div>
          <p className="mt-1 text-sm text-secondary-600">{app.tag}</p>
          <p className="mt-4 leading-relaxed text-foreground-700">{app.desc}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {app.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                {t}
              </span>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-background-200/70 pt-4">
            <span className="text-sm text-foreground-600">
              <i className="ri-download-2-line mr-1"></i>
              {app.downloads}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600"
            >
              返回榜单
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Ranking() {
  const [active, setActive] = useState<AppItem | null>(null);
  const [favorites, setFavorites] = useState<number[]>([]);

  const toggleFavorite = (id: number) =>
    setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <section id="ranking" className="section-x py-14 md:py-20">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700">
            <span className="h-2 w-2 rounded-full bg-primary-500"></span>
            热门平台榜单
          </p>
          <h2 className="mt-2 font-heading text-3xl text-foreground-900 md:text-4xl">大家都在用的漫画阅读软件</h2>
          <p className="mt-3 max-w-2xl text-foreground-600">
            依据资源广度、更新速度、画质与使用体验综合整理，全部为支持正版内容的规范平台。
          </p>
        </div>
        <span className="hidden text-sm text-foreground-500 md:block">
          共收录 <strong className="text-foreground-800">{apps.length}</strong> 个精选平台
        </span>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {apps.map((app) => {
          const fav = favorites.includes(app.id);
          return (
            <article
              key={app.id}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-100/60 transition-colors hover:border-primary-200"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={app.image}
                  alt={`${app.name} 漫画平台推荐`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/40 to-transparent"></div>
                <span className="absolute left-3 top-3 flex h-8 items-center rounded-md bg-background-50/90 px-2.5 font-heading text-lg text-primary-700">
                  #{app.rank}
                </span>
                {app.isHot && (
                  <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-xs text-background-50">
                    <i className="ri-fire-fill"></i>热门
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => toggleFavorite(app.id)}
                  className={`absolute bottom-3 right-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full backdrop-blur-sm ${
                    fav ? "bg-primary-500 text-background-50" : "bg-background-50/20 text-background-50"
                  }`}
                  aria-label={fav ? "取消收藏" : "收藏"}
                >
                  <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
                </button>
              </div>

              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground-900">{app.name}</h3>
                    <p className="mt-0.5 text-sm text-secondary-600">{app.tag}</p>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-sm text-accent-800">
                    <i className="ri-star-fill text-accent-600"></i>
                    {app.rating}
                  </span>
                </div>

                <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">{app.desc}</p>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {app.tags.map((t) => (
                    <span key={t} className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-auto flex items-center justify-between border-t border-background-200/70 pt-4">
                  <span className="text-xs text-foreground-500">
                    <i className="ri-download-2-line mr-1"></i>
                    {app.downloads}
                  </span>
                  <button
                    type="button"
                    onClick={() => setActive(app)}
                    className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    查看详情
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {active && <AppModal app={active} onClose={() => setActive(null)} />}
    </section>
  );
}